import {AfterViewInit, Component, OnInit, QueryList, ViewChild, ViewChildren} from "@angular/core";
import {Checkbox, ConfirmationService, MenuItem, Message, SelectItem} from "primeng/primeng";
import {MessageService} from "primeng/components/common/messageservice";
import {UserService} from "@nordpoolgroup/nps-angular-2-common";
import {Subject} from "rxjs/Subject";
import "rxjs/add/observable/fromPromise";
import "rxjs/add/observable/interval";
import * as moment from "moment-timezone";

import {Names} from "../names";
import {AreaDomainService} from "./Services/AreaDomainService";
import {ContractDataService} from "./Services/ContractDataService";
import {DateTimeService} from "../Shared/DateTimeService";
import {NpmServiceAdapter} from "./Services/NpmServiceAdapter";
import {XBidServiceAdapter} from "./Services/XBidServiceAdapter";
import * as ContractDataModels from "./ContractDataModels";
import {ContractState} from "./ContractDataModels";
import * as ContractDataSchema from "./Services/IContractData";
import {Matcher} from "./Services/IContractData";
import {TenantService} from "./Services/TenantService";
import {NpmBackupServiceAdapter} from "./Services/NpmBackupServiceAdapter";

@Component({
    selector: Names.contractManagement.selector,
    template: require("./contractManagement.html"),
    providers: [
        AreaDomainService,
        ContractDataService,
        DateTimeService,
        MessageService,
        UserService,
        XBidServiceAdapter,
        NpmServiceAdapter,
        NpmBackupServiceAdapter,
        TenantService
    ]
})
export class ContractManagementComponent implements OnInit, AfterViewInit {
    private static readonly AVAILABLE_PRODUCTS: ContractDataSchema.ContractType[] = [
        "P15MIN",
        "P30MIN",
        "UK_P30MIN",
        "P60MIN",
        "BLOCK_2H",
        "BLOCK_4H",
        "DON",
        "DB34",
        "DP",
        "DEP",
        "DB",
        "CUSTOM_BLOCK"
    ];

    private static readonly BACKUP_AREAS: string[] = [
        "DK1", "DK2", "FI", "EE", "LT", "LV", "NO1", "NO2", "NO3", "NO4", "NO5", "PL",
        "SE1", "SE2", "SE3", "SE4", "NL", "BE", "TBW", "AMP", "TTG", "50Hz", "FR", "AT", "BG", "HR", "TEL", "ELES"
    ];
    private static readonly DATA_POLLING_INTERVAL = 10000;
    private dataPollingTimerId: number;

    dataChangeEmitter: Subject<ContractDataSchema.IContractData[]>;
    visibleContractTemplates: ContractDataModels.ContractTemplate[] = [];
    contracts: ContractDataSchema.IContractData[] = [];
    areas: NPS.AreaDomain.IAreaDomain[] = [];

    get hasChangedToBeSaved(): boolean {
        return this.changedContractAreas.length > 0;
    }

    @ViewChildren("areaSelector") areaSelectors: QueryList<Checkbox>;
    @ViewChild("allAreasSelector") allAreaSelector: Checkbox;

    availableProducts: SelectItem[] = ContractManagementComponent.AVAILABLE_PRODUCTS.map(product => ({label: product, value: product}));
    selectedProducts: ContractDataSchema.ContractType[] = _.values(ContractManagementComponent.AVAILABLE_PRODUCTS);

    availableMatchers: SelectItem[] = this.getAvailableMatchers();
    activeMatcher: Matcher = this.getInitActiveMatcher();

    gridContextMenuItems: MenuItem[];

    private changedContractAreas: ContractDataSchema.IContractEditData[] = [];
    private selectedContractAreas: { [contractId: string]: string[] } = {};

    snapshotFetched = false;
    savingInProgress = false;
    pollingInProgress = false;
    showActivateAndDeactivateAllTenantsButton = false;
    showActivateAndDeactiveBackupButton = false;
    showXbidConnectionAndloginButton = false;
    showCloseAndSaveContractButton = false;
    showSaveChangesButton = false;
    marketType: string;


    $tblBody: JQuery;

    private shiftKeyStartArea: string;
    private shiftKeyStartRow: number;

    private areAllContractsClosed: boolean;
    private areAllContractsNotFrozen: boolean;
    private statusChangingIsInProgress: boolean;
    private openingAllContractsIsInProgress: boolean;
    private closingAllContractsIsInProgress: boolean;

    private npmContracts: Array<ContractDataSchema.IContractData>;
    private XBIDContracts: Array<ContractDataSchema.IContractData>;

    readonly notifications: Message[] = [];

    private wholeAreaSelections: { [areaCode: string]: boolean } = {};

    private contractTemplates: ContractDataModels.ContractTemplate[] = [];
    visibleContractTemplatesByDay: ContractDataModels.ContractTemplateGroup[] = [];

    private static getContractTemplateId(contract: ContractDataSchema.IContractData): string {
        return `${contract.reportProductCode}-${moment(contract.contractDate).valueOf()}`;
    }

    constructor(
        private readonly contractService: ContractDataService,
        private readonly areaDomainService: AreaDomainService,
        private readonly dateTimeService: DateTimeService,
        private readonly messageService: MessageService,
        private readonly userService: UserService,
        private readonly confirmationService: ConfirmationService,
        private readonly xbidServiceAdapter: XBidServiceAdapter,
        private readonly npmBackupServiceAdapter: NpmBackupServiceAdapter,
        private readonly tenantService: TenantService
    ) {
        this.contractService.activeMatcher = this.activeMatcher;
        this.gridContextMenuItems = this.generateAvailableActions();
        this.initDataChangeEmitter();
        this.checkStatusOfAllContracts();
    }

    checkStatusOfAllContracts(): void {
        if (this.contractService.activeMatcher === ContractDataSchema.Matcher.NPM) {
            this.npmContracts = this.contracts.filter(contract => contract.tenantId === this.tenantService.currentTenantId);
            this.areAllContractsClosed = this.contracts.length > 0
                && !this.visibleContractTemplatesByDay
                    .reduce((acc: ContractDataModels.ContractTemplate[], g) => acc.concat(g.rows), [])
                    .reduce((acc: ContractDataModels.ContractArea[], r) => acc.concat(Array.from(r.areas.values())), [])
                    .some(area => area.status === "OPEN");
        } else if (this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID) {
            this.XBIDContracts = this.contracts.filter(contract => this.tenantService.isXbid(contract.tenantId));
            this.areAllContractsClosed = this.contracts.length > 0
                && !this.visibleContractTemplatesByDay
                    .reduce((acc: ContractDataModels.ContractTemplate[], g) => acc.concat(g.rows), [])
                    .reduce((acc: ContractDataModels.ContractArea[], r) => acc.concat(Array.from(r.areas.values())), [])
                    .some(area => area.status === "OPEN");
        } else {
            this.areAllContractsClosed = false;
        }
    }

    checkStatusOfAllFrozenContracts(): void {
        if (this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID) {
            this.XBIDContracts = this.contracts.filter(contract => this.tenantService.isXbid(contract.tenantId));
            this.areAllContractsNotFrozen = this.contracts.length > 0 && !this.XBIDContracts.some(contract => contract.contractStatus === "FROZEN");
        } else {
            this.areAllContractsNotFrozen = false;
        }
    }

    changeStatusOfAllContracts(): void {
        this.statusChangingIsInProgress = true;
        let saveRequest: Promise<void>;

        this.setVisibleAreas();

        if (this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID) {
            const newXBIDStatus = this.areAllContractsClosed ? ContractState.OPEN : ContractState.CLOSE;
            if (newXBIDStatus === ContractState.OPEN) {
                this.openingAllContractsIsInProgress = true;
            } else {
                this.closingAllContractsIsInProgress = true;
            }
            saveRequest = this.contractService.contractManagementRequestByMatcher(newXBIDStatus, ContractDataSchema.Matcher.XBID);

        } else {
            let changedNpmContracts: ContractDataSchema.IContractEditData[] = [];
            const newNpmStatus = this.areAllContractsClosed ? "OPEN" : "CLOSED";
            if (newNpmStatus === "OPEN") {
                this.openingAllContractsIsInProgress = true;
            } else {
                this.closingAllContractsIsInProgress = true;
            }

            const marketAreaCodesSet = new Set(this.areas.map(area => area.code));
            const marketAreaCodes = Array.from(marketAreaCodesSet);

            this.npmContracts.forEach((contract) => {
                if (this.isStatusEditable(contract.contractStatus) && contract.contractStatus !== newNpmStatus) {
                    changedNpmContracts.push({
                        id: contract.id,
                        contractStatus: newNpmStatus,
                        marketAreaCode: contract.marketAreaCode,
                        tenantId: contract.tenantId
                    });
                }
            });

            saveRequest = this.contractService.saveByMatcher(changedNpmContracts, marketAreaCodes, ContractDataSchema.Matcher.NPM).then((response) => {
                if (this.contractService.activeMatcher === ContractDataSchema.Matcher.NPM) {
                    this.fetchContracts();
                }
                changedNpmContracts = [];
            });
        }

        this.savingInProgress = true;

        Promise.all([
            saveRequest
        ]).then((response) => {
            this.savingInProgress = false;
            this.updateWholeAreaSelectorStates();
            this.checkStatusOfAllContracts();
        }).catch((error) => {
            this.savingInProgress = false;
            this.messageService.add({
                severity: "error",
                summary: "Saving failed",
                detail: "Unexpected problem while saving!"
            });
        });
    }

    isStatusEditable(status: string): boolean {
        switch (status) {
            case "OPEN":
            case "CLOSED":
            case "FROZEN":
                return true;
            default:
                return false;
        }
    }

    ngOnInit(): void {
        this.showUiComponents({value: this.activeMatcher});
        Promise.all([this.areaDomainService.getByTenantIdAndMarketType(this.tenantService.currentTenantId, this.marketType)])
            .then((responses) => {
                this.areas = responses[0];
                this.setVisibleAreas();
                return this.fetchContracts();
            })
            .then(() => {
                this.updateWholeAreaSelectorStates();
                this.startPolling();
            })
            .catch((error) => {
                console.error(`Error fetching initial data: ${error}`);
            });
    }

    // TODO: Refactor this completely
    ngAfterViewInit(): void {
        const $tbl = $("#contractTable");
        const $tblHead = $tbl.find("thead");
        this.$tblBody = $tbl.find("tbody");
        const $thNthChild1 = $tblHead.find("th:nth-child(1)");

        let previousScrollLeft: number = null;

        this.$tblBody.scroll((e: any) => { // detect a scroll event on the tbody
            const scrollLeft = this.$tblBody.scrollLeft();
            if (scrollLeft === previousScrollLeft) {
                return;
            }
            previousScrollLeft = scrollLeft;
            $tblHead.css("left", -scrollLeft); // fix the thead relative to the body scrolling
            $thNthChild1.css("left", scrollLeft); // fix the first cell of the header
            // We must query tds here instead of caching them like with th above, because at the moment of ngAfterViewInit is called
            // table does not have any content.
            this.$tblBody.find("td:nth-child(1)").css("left", scrollLeft); // fix the first column of tdbody
        });

        const $container = $("#containerContainer");
        $tbl.width($container.width());
        this.$tblBody.height($container.height());
        $(window).resize(() => {
            $tbl.width($container.width());
            this.$tblBody.height($container.height());
        });
    }

    private initDataChangeEmitter(): void {
        const subject = new Subject<ContractDataSchema.IContractData[]>();
        subject.subscribe(contracts => {
            this.contracts = contracts;
            this.generateContractTemplates(contracts);
            this.updateVisibleContracts();
            this.checkStatusOfAllContracts();
            this.checkStatusOfAllFrozenContracts();
        });
        this.dataChangeEmitter = subject;
    }

    private startPolling(): void {
        if (typeof this.dataPollingTimerId !== "number") {
            clearInterval(this.dataPollingTimerId);
            this.dataPollingTimerId = null;
        }

        this.dataPollingTimerId = window.setInterval(() => {
            this.fetchContracts();
        }, ContractManagementComponent.DATA_POLLING_INTERVAL);
    }

    private fetchContracts(): Promise<ContractDataSchema.IContractData[]> {
        if (!this.savingInProgress && !this.pollingInProgress) {
            this.pollingInProgress = true;

            const getContracts = this.contractService.getContractsByMatcher(this.activeMatcher);
            getContracts
                .then(contracts => {
                    if (!this.savingInProgress) {
                        this.dataChangeEmitter.next(contracts);
                        this.snapshotFetched = true;
                    }
                    this.pollingInProgress = false;
                    this.handleOpenCloseAllContracts();
                })
                .catch(error => {
                    this.messageService.add({
                        summary: "Problem loading contracts",
                        detail: "Unexpected problem while loading initial data!",
                        severity: "error"
                    });
                    this.pollingInProgress = false;

                    throw error;
                });

            return getContracts;
        } else {
            return Promise.resolve([]);
        }
    }

    private handleOpenCloseAllContracts(): void {
        const toggleIfConditionValue = this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID
            ? this.areAllContractsNotFrozen : !this.areAllContractsClosed;
        if (this.areAllContractsClosed && this.closingAllContractsIsInProgress) {
            this.statusChangingIsInProgress = false;
            this.closingAllContractsIsInProgress = false;
            if (this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID) {
                this.openSavingToastMessage("CLOSE", "XBID");
            } else {
                this.openSavingToastMessage("CLOSE", "NPM");
            }
        } else if (toggleIfConditionValue && this.openingAllContractsIsInProgress) {
            this.statusChangingIsInProgress = false;
            this.openingAllContractsIsInProgress = false;
            if (this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID) {
                this.openSavingToastMessage("OPEN", "XBID");
            } else {
                this.openSavingToastMessage("OPEN", "NPM");
            }
        }
    }

    private generateContractTemplates(contracts: ContractDataSchema.IContractData[]): void {
        const contractTemplates: { [idString: string]: ContractDataModels.ContractTemplate } = {};

        contracts.forEach((contract, i) => {
            if (contract.tenantId !== this.tenantService.currentTenantId) {
                return;
            }

            const marketAreaCode = this.contractService.activeMatcher === ContractDataSchema.Matcher.XBID
                ? contract.dlvryAreaId.toString()
                : contract.marketAreaCode;
            const contractId = ContractManagementComponent.getContractTemplateId(contract);
            const contractTemplate = contractTemplates[contractId]
                ? contractTemplates[contractId]
                : contractTemplates[contractId] = new ContractDataModels.ContractTemplate(contract, this.dateTimeService);

            if (!this.areas.find(area => area.code === contract.marketAreaCode)) {
                return;
            }

            const editedContractAreaIndex = this.changedContractAreas.findIndex(
                change => change.marketAreaCode === marketAreaCode && change.id === contract.id
            );
            const isContractEditable = this.contractService.isContractStatusEditable(contract.contractStatus);

            contractTemplate.areas.set(contract.marketAreaCode, {
                selected: this.selectedContractAreas[contract.id] && this.selectedContractAreas[contract.id].indexOf(marketAreaCode) >= 0,
                status: isContractEditable && editedContractAreaIndex >= 0
                    ? this.changedContractAreas[editedContractAreaIndex].contractStatus
                    : contract.contractStatus,
                persistedStatus: contract.contractStatus,
                contractId: contract.id,
                marketAreaCode: marketAreaCode,
                tenantId: contract.tenantId
            });

            if (editedContractAreaIndex >= 0
                && (!isContractEditable || this.changedContractAreas[editedContractAreaIndex].contractStatus === contract.contractStatus)
            ) {
                this.changedContractAreas.splice(editedContractAreaIndex, 1);
            }
        });

        this.contractTemplates = _.values(contractTemplates).sort(this.contractTemplateComparator);
    }

    private updateVisibleContracts() {
        this.visibleContractTemplates = this.contractTemplates.filter(contractTemplate => {
            const productIsVisible = this.selectedProducts.includes(contractTemplate.contractType);

            if (!productIsVisible) {
                contractTemplate.areas.forEach(areaStatus => {
                    this.selectContractArea(areaStatus, false);

                    if (areaStatus.status !== areaStatus.persistedStatus) {
                        areaStatus.status = areaStatus.persistedStatus;

                        const contractAreaChangeIndex = this.changedContractAreas.findIndex(
                            areaChange => areaChange.id === areaStatus.contractId && areaChange.marketAreaCode === areaStatus.contractId
                        );
                        this.changedContractAreas.splice(contractAreaChangeIndex, 1);
                    }
                });
            }

            return productIsVisible;
        });

        const contractTemplatesByDay: ContractDataModels.ContractTemplateGroup[] = [];
        let currentDay: string;
        let currentDayContractTemplates: ContractDataModels.ContractTemplate[] = [];
        this.visibleContractTemplates.forEach(contractTemplate => {
            if (currentDay !== contractTemplate.day) {
                if (currentDayContractTemplates.length) {
                    contractTemplatesByDay.push({
                        day: currentDay,
                        rows: currentDayContractTemplates
                    });
                }

                currentDay = contractTemplate.day;
                currentDayContractTemplates = [];
            }

            currentDayContractTemplates.push(contractTemplate);
        });

        if (currentDayContractTemplates.length) {
            contractTemplatesByDay.push({
                day: currentDay,
                rows: currentDayContractTemplates
            });
        }

        this.visibleContractTemplatesByDay = contractTemplatesByDay;

        // Refresh horizontal scrolling
        this.$tblBody.find("td:nth-child(1)").css("left", this.$tblBody.scrollLeft());
    }

    onCellClick(event: MouseEvent, contract: ContractDataModels.ContractTemplate, areaCode: string): void {
        const rowIndex = this.visibleContractTemplates.findIndex(visibleContract => visibleContract === contract);

        if (!event.ctrlKey) {
            this.deselectAll();
        } else {
            if (!event.shiftKey) { // CTRL key pressed
                const areaToToggle = this.visibleContractTemplates[rowIndex].areas.get(areaCode);
                this.selectRange([areaCode], rowIndex, rowIndex, areaToToggle && !areaToToggle.selected);

                return;
            }
        }

        if ((event.shiftKey && _.isNull(this.shiftKeyStartArea) && _.isNull(this.shiftKeyStartRow)) || !event.shiftKey) {
            this.shiftKeyStartArea = areaCode;
            this.shiftKeyStartRow = rowIndex;
        }

        const selectionStartColumnIndex = this.areas.findIndex(area => area.code === this.shiftKeyStartArea);
        const selectionEndColumnIndex = this.areas.findIndex(area => area.code === areaCode);

        const areaRange = this.areas.slice(
            Math.min(selectionStartColumnIndex, selectionEndColumnIndex),
            Math.max(selectionStartColumnIndex, selectionEndColumnIndex) + 1
        ).map(area => area.code);

        const startRow = Math.min(this.shiftKeyStartRow, rowIndex);
        const endRow = Math.max(this.shiftKeyStartRow, rowIndex);

        this.selectRange(areaRange, startRow, endRow, true);
    }

    private selectRange(areaCodes: string[], firstRow: number, lastRow: number, selected: boolean, updateAreaWholeSelectedState: boolean = true): void {
        for (let i = firstRow; i <= lastRow; i += 1) {
            const contract = this.visibleContractTemplates[i];
            areaCodes.forEach(areaCode => {
                const contractArea = contract.areas.get(areaCode);

                if (!contractArea) {
                    return;
                }

                this.selectContractArea(contractArea, this.contractService.isContractStatusEditable(contractArea.persistedStatus) && selected);
            });
        }

        this.updateWholeAreaSelectorStates(areaCodes, updateAreaWholeSelectedState);
    }

    private updateWholeAreaSelectorStates(modifiedAreaCodes?: string[], updateModifiedAreaWholeSelectedState: boolean = true) {
        const areasToCheck = modifiedAreaCodes ? modifiedAreaCodes : this.areas.map(area => area.code);

        areasToCheck.forEach(areaCode => {
            if (modifiedAreaCodes && !updateModifiedAreaWholeSelectedState && modifiedAreaCodes.includes(areaCode)) {
                return;
            }

            this.wholeAreaSelections[areaCode] = _.all<ContractDataModels.ContractArea>(
                this.visibleContractTemplates.map(contractTemplate => contractTemplate.areas.get(areaCode)),
                status => !status || !this.contractService.isContractStatusEditable(status.persistedStatus) || status.selected
            );
        });

        this.allAreaSelector.checked = _.all<boolean>(_.values(this.wholeAreaSelections));
    }

    toggleAllAreasSelection(checked: boolean) {
        this.visibleContractTemplates.forEach(contractTemplate => {
            contractTemplate.areas.forEach((areaStatus, areaCode) => {
                this.selectContractArea(areaStatus, checked && this.contractService.isContractStatusEditable(areaStatus.persistedStatus));
            });
        });

        Object.getOwnPropertyNames(this.wholeAreaSelections).forEach(areaCode => {
            this.wholeAreaSelections[areaCode] = checked;
        });
        this.allAreaSelector.checked = checked;
    }

    toggleWholeAreaSelection(checked: boolean, area: string) {
        // The last parameter is needed for the case when there is no editable cell in the given area
        this.selectRange([area], 0, this.visibleContractTemplates.length - 1, checked, false);
    }

    private deselectAll() {
        this.toggleAllAreasSelection(false);
    }

    productSelectionChanged(event: any): void {
        if (this.hasChangedToBeSaved) {
            this.confirmationService.confirm({
                message: "You have not yet saved changes you have made. Would you like to save changes now?",
                accept: () => {
                    this.saveChanges();
                },
                reject: () => {
                    this.changedContractAreas = [];
                    this.updateVisibleContracts();
                    this.updateWholeAreaSelectorStates();
                }
            });
        } else {
            this.updateVisibleContracts();
            this.updateWholeAreaSelectorStates();
        }
    }

    matcherSelectionChanged(event: any): void {
        this.showUiComponents(event);
        this.deselectAll();
        this.shiftKeyStartArea = null;
        this.shiftKeyStartRow = null;
        this.gridContextMenuItems = this.generateAvailableActions();

        if (this.hasChangedToBeSaved) {
            this.confirmationService.confirm({
                message: "You have not yet saved changes you have made. Would you like to save changes now?",
                accept: () => {
                    this.saveChanges();
                },
                reject: () => {
                    this.changedContractAreas = [];
                    this.fetchAreasAndContracts();
                }
            });
        } else {
            this.fetchAreasAndContracts();
        }
    }

    private showUiComponents(event: any): void {
        switch (event.value) {
            case 0 : {
                this.marketType = Matcher[Matcher.NPM];
                this.contractService.activeMatcher = Matcher.NPM;
                this.activeMatcher = Matcher.NPM;
                this.showXbidConnectionAndloginButton = false;
                this.showActivateAndDeactivateAllTenantsButton = false;
                this.showActivateAndDeactiveBackupButton = false;
                this.showCloseAndSaveContractButton = true;
                this.showSaveChangesButton = true;
                break;
            }
            case 1 : {
                this.marketType = Matcher[Matcher.XBID];
                this.contractService.activeMatcher = Matcher.XBID;
                this.activeMatcher = Matcher.XBID;
                this.showXbidConnectionAndloginButton = true;
                this.showActivateAndDeactivateAllTenantsButton = false;
                this.showActivateAndDeactiveBackupButton = false;
                this.showCloseAndSaveContractButton = true;
                this.showSaveChangesButton = true;
                break;
            }
            case 2 : {
                this.marketType = Matcher[Matcher.NPM];
                this.contractService.activeMatcher = Matcher.Backup;
                this.activeMatcher = Matcher.Backup;
                this.showXbidConnectionAndloginButton = false;
                this.showActivateAndDeactivateAllTenantsButton = this.tenantService.isNordpoolTenant();
                this.showActivateAndDeactiveBackupButton = true;
                this.showCloseAndSaveContractButton = false;
                this.showSaveChangesButton = true;
                break;
            }
        }
    }

    private fetchAreasAndContracts(): void {
        Promise.all([this.areaDomainService.getByTenantIdAndMarketType(this.tenantService.currentTenantId, this.marketType)])
            .then((responses) => {
                if (this.contractService.activeMatcher === Matcher.Backup) {
                    this.areas = responses[0].filter(response => ContractManagementComponent.BACKUP_AREAS.includes(response.code));
                } else {
                    this.areas = responses[0];
                }
                this.setVisibleAreas();

                return this.fetchContracts();
            })
            .then(() => {
                this.updateWholeAreaSelectorStates();
            })
            .catch((error) => {
                console.error(`Error fetching initial data: ${error}`);
            });
    }

    private setVisibleAreas() {
        this.areas.sort(this.areaComparator);
        this.areas.forEach(area => {
            this.wholeAreaSelections[area.code] = true;
        });
        this.allAreaSelector.checked = true;
    }

    private contractTemplateComparator(a: ContractDataModels.ContractTemplate, b: ContractDataModels.ContractTemplate): number {
        const diff = a.deliveryStart.diff(b.deliveryStart);

        if (diff !== 0) {
            return diff;
        } else {
            return (a.productCode > b.productCode) ? 1 : -1;
        }
    }

    private areaComparator(a: NPS.AreaDomain.IAreaDomain, b: NPS.AreaDomain.IAreaDomain): number {
        return (a.code > b.code) ? 1 : -1;
    }

    saveChanges(): void {
        this.savingInProgress = true;

        this.contractService.save(this.changedContractAreas, [])
            .then(contracts => {
                this.changedContractAreas = [];

                this.savingInProgress = false;
                this.messageService.add({
                    severity: "success",
                    summary: "Saving completed",
                    detail: "All changes successfully saved!"
                });

                // Get refresh state
                this.fetchContracts();
                this.checkStatusOfAllContracts();

            })
            .catch(error => {
                this.savingInProgress = false;
                this.messageService.add({severity: "error", summary: "Saving failed", detail: "Unexpected problem while saving!"});
                this.checkStatusOfAllContracts();
            });
    }

    changeStatusOfSelectedContractAreas(newStatus: string) {
        this.visibleContractTemplates.forEach(contractTemplate => contractTemplate.areas.forEach(contractArea => {
            if (contractArea.selected) {
                const editedContractAreaIndex = this.changedContractAreas.findIndex(editedContractArea =>
                    editedContractArea.id === contractArea.contractId && editedContractArea.marketAreaCode === contractArea.marketAreaCode);

                if (editedContractAreaIndex >= 0) {
                    if (newStatus === contractArea.persistedStatus) {
                        this.changedContractAreas.splice(editedContractAreaIndex, 1);
                    }
                } else if (newStatus !== contractArea.persistedStatus) {
                    this.changedContractAreas.push({
                        id: contractArea.contractId,
                        contractStatus: newStatus,
                        marketAreaCode: contractArea.marketAreaCode,
                        tenantId: contractArea.tenantId
                    });
                }

                contractArea.status = newStatus;
            }
        }));
    }

    openSavingToastMessage(status: string, marketType: string): void {
        if (status === "OPEN") {
            this.messageService.add({severity: "success", summary: "Saving completed", detail: `All ${marketType} contracts are open!`});
        } else {
            this.messageService.add({severity: "success", summary: "Saving completed", detail: `All ${marketType} contracts are closed!`});
        }
    }

    private selectContractArea(contractArea: ContractDataModels.ContractArea, selected: boolean) {
        if (contractArea.selected === selected) {
            return;
        }

        contractArea.selected = selected;
        if (selected) {
            this.selectedContractAreas[contractArea.contractId] = this.selectedContractAreas[contractArea.contractId] || [];
            if (!this.selectedContractAreas[contractArea.contractId]
                .find(selectedContractAreaCode => selectedContractAreaCode === contractArea.marketAreaCode)
            ) {
                this.selectedContractAreas[contractArea.contractId].push(contractArea.marketAreaCode);
            }
        } else if (this.selectedContractAreas[contractArea.contractId]) {
            const i = this.selectedContractAreas[contractArea.contractId].findIndex(
                selectedContractAreaCode => selectedContractAreaCode === contractArea.marketAreaCode
            );

            if (i >= 0) {
                this.selectedContractAreas[contractArea.contractId].splice(i, 1);
            }
            if (!this.selectedContractAreas[contractArea.contractId].length) {
                delete this.selectedContractAreas[contractArea.contractId];
            }
        }
    }

    private generateAvailableActions(): MenuItem[] {
        return this.contractService.availableActions.map(action => ({
            label: action.label,
            command: (event: any) => {
                this.changeStatusOfSelectedContractAreas(action.status);
            }
        }));
    }

    trackFnArea(index: number, item: NPS.AreaDomain.IAreaDomain): string {
        return item.code;
    }

    trackFnRowGroup(index: number, item: ContractDataModels.ContractTemplateGroup): string {
        return item.day;
    }

    trackFnRow(index: number, item: ContractDataModels.ContractTemplate): string {
        return item.productCode;
    }

    getAvailableMatchers(): SelectItem[] {
        const availableMatchers = new Set<SelectItem>();
        if (this.tenantService.isNPMContractsEnabledForCurrentTenant()) {
            availableMatchers.add({label: "NPM", value: Matcher.NPM});
        }
        if (this.tenantService.isXBIDContractsEnabledForCurrentTenant()) {
            availableMatchers.add({label: "XBID", value: Matcher.XBID});
        }
        if (this.tenantService.isBackupContractsEnabledForCurrentTenant()) {
            availableMatchers.add({label: "Backup", value: Matcher.Backup});
        }
        return Array.from(availableMatchers.values());
    }

    getInitActiveMatcher(): Matcher {
        return this.getAvailableMatchers()[0].value;
    }
}



// WEBPACK FOOTER //
// ./src/app/ContractManagement/ContractManagementComponent.ts